import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import { KPIS_QUERY_KEY } from './useKpis';
import { CAJAS_QUERY_KEY } from './useCajas';
import { MOVIMIENTOS_QUERY_KEY } from './useMovimientos';
import { CHART_QUERY_KEY } from './useChartData';
import { COBROS_PENDIENTES_QUERY_KEY } from './useCobrosPendientes';
import type { ArbolCobrosPendientes, Moneda, PagoPayload } from '../types';

/** Cuelga de COBROS_PENDIENTES_QUERY_KEY: invalidar esa clave también refresca el árbol. */
export const ARBOL_COBROS_QUERY_KEY = [...COBROS_PENDIENTES_QUERY_KEY, 'arbol'] as const;

export function useArbolCobros() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<ArbolCobrosPendientes>({
    queryKey: ARBOL_COBROS_QUERY_KEY,
    queryFn: () => contabilidadService.getArbolCobros(),
    staleTime: 0,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: COBROS_PENDIENTES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: KPIS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: MOVIMIENTOS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: CHART_QUERY_KEY });
  };

  const cobrarAgrupadorMutation = useMutation({
    mutationFn: ({ id, moneda, payload, cliente }: { id: number; moneda: Moneda; payload: PagoPayload; cliente?: string }) =>
      contabilidadService.cobrarAgrupador(id, moneda, payload, cliente),
    onSuccess: invalidar,
  });

  const cobrarItemMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PagoPayload }) =>
      contabilidadService.cobrarItemEscritura(id, payload),
    onSuccess: invalidar,
  });

  return {
    arbol: data,
    isLoading,
    error,
    cobrarAgrupador: cobrarAgrupadorMutation.mutateAsync,
    cobrarItem: cobrarItemMutation.mutateAsync,
    isCobrando: cobrarAgrupadorMutation.isPending || cobrarItemMutation.isPending,
  };
}
